import { Injectable, signal, DestroyRef, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../../environments/environment';
import { tap } from 'rxjs';
import { withIdempotencyKey } from './idempotency.interceptor';
import { SKIP_AUTH_LOGOUT, SKIP_ERROR_TOAST } from './http-context.tokens';

/** Factor del segundo factor (step-up) para aprobar. */
export type StepUpFactor = 'totp' | 'password';

export interface StepUpDesafio {
  desafioId: string;
  factorRequerido: StepUpFactor;
  expiraEn: string;
  intentosRestantes: number;
}

export interface DecisionAprobador {
  userId: string;
  nombre: string;
  email: string;
  decision: string;
  comentario: string;
  fecha: string;
}

export interface IntentoAprobacion {
  numero: number;
  aprobadores: DecisionAprobador[];
  estadoFinal: 'aprobada' | 'rechazada';
  fechaInicio: string;
  fechaFin?: string;
}

/**
 * Moneda real de la operación aprobada. Ausente en documentos legacy ⇒ tratar como ARS.
 * `USD`/`USDC` en préstamos, `USD_CABLE`/`USD_LOCAL`/`USD_MEP` en compra de divisas.
 */
export type AprobacionMoneda = 'ARS' | 'USD' | 'USDC' | 'USD_CABLE' | 'USD_LOCAL' | 'USD_MEP';

/** Origen de la cotización usada para convertir `monto` a `montoEquivalenteArs`. */
export type FuenteTipoCambio = 'ars' | 'operacion' | 'referencia' | 'sin_cotizacion';

export interface Aprobacion {
  _id: string;
  entidad: 'ordenes-pago' | 'pagos' | 'prestamos' | 'compras-divisas' | 'pagos-programados' | 'instrucciones-tesoreria';
  entidadId: string;
  tipo: string;
  estado: 'pendiente' | 'aprobada' | 'rechazada';
  aprobadores: DecisionAprobador[];
  aprobacionesRequeridas: number;
  monto: number;
  moneda?: AprobacionMoneda;
  // Equivalente en ARS usado para evaluar las reglas de aprobación (rate propio de la
  // operación o de referencia). `null`/ausente cuando no aplica (aprobación en ARS) o
  // no hay cotización disponible (`fuenteTipoCambio === 'sin_cotizacion'`).
  montoEquivalenteArs?: number | null;
  tipoCambioAplicado?: number | null;
  fuenteTipoCambio?: FuenteTipoCambio;
  descripcion: string;
  createdBy: string;
  createdByEmail: string;
  datosOperacion?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  // Campos de reenvío
  intentos: IntentoAprobacion[];
  reenviosRestantes: number;
  fechaReenvio?: string;
  reenviadoPor?: string;
}

export interface ContextoToken {
  tipo: string;
  entidad: string;
  descripcion: string;
  monto: number;
  moneda?: AprobacionMoneda;
  montoEquivalenteArs?: number | null;
  tipoCambioAplicado?: number | null;
  fuenteTipoCambio?: FuenteTipoCambio;
  solicitante: string;
  fechaSolicitud: string;
  expiraEn: string;
  aprobadorEmail: string;
}

@Injectable({ providedIn: 'root' })
export class AprobacionService {
  private url = `${environment.apiUrl}/aprobaciones`;
  private _pendingCount = signal(0);
  private destroyRef = inject(DestroyRef);
  pendingCount = this._pendingCount.asReadonly();

  constructor(private http: HttpClient) {}

  loadPendingCount() {
    this.http.get<number>(`${this.url}/count`).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(count => this._pendingCount.set(count));
  }

  /** Limpia el estado cacheado. Se invoca en el logout para no filtrar datos entre sesiones. */
  reset() {
    this._pendingCount.set(0);
  }

  getPendientes() {
    return this.http.get<Aprobacion[]>(`${this.url}/pendientes`);
  }

  getAll() {
    return this.http.get<Aprobacion[]>(this.url);
  }

  getById(id: string) {
    return this.http.get<Aprobacion>(`${this.url}/${id}`);
  }

  getByEntity(entidad: string, entidadId: string) {
    return this.http.get<Aprobacion[]>(`${this.url}/entidad/${entidad}/${entidadId}`);
  }

  // Decidir aprueba/ejecuta plata: idempotencia anti doble-submit (header Idempotency-Key
  // via idempotencyInterceptor). El `idempotencyKey` lo genera el componente por intento.
  decidir(
    id: string,
    decision: string,
    comentario?: string,
    idempotencyKey?: string,
    stepUp?: { desafioId: string; stepUpProof: string },
  ) {
    return this.http.patch<Aprobacion>(
      `${this.url}/${id}/decidir`,
      { decision, comentario, ...(stepUp ?? {}) },
      idempotencyKey ? { context: withIdempotencyKey(idempotencyKey) } : {},
    ).pipe(
      tap(() => this.loadPendingCount()),
    );
  }

  // Step-up (segundo factor) por JWT cuando la aprobación lo exige. El 401 de estos
  // endpoints es parte del flujo (factor requerido / código incorrecto), así que van
  // con SKIP_AUTH_LOGOUT para que el interceptor NO desloguee al aprobador.
  private stepUpContext(): HttpContext {
    return new HttpContext()
      .set(SKIP_AUTH_LOGOUT, true)
      .set(SKIP_ERROR_TOAST, true);
  }

  /** Inicia el desafío de segundo factor para aprobar (devuelve el factor a pedir). */
  iniciarStepUp(id: string): Observable<StepUpDesafio> {
    return this.http.post<StepUpDesafio>(
      `${this.url}/${id}/step-up/iniciar`,
      {},
      { context: this.stepUpContext() },
    );
  }

  /** Verifica el segundo factor y devuelve el proof single-use para `decidir`. */
  verificarStepUp(
    id: string,
    desafioId: string,
    secreto: string,
  ): Observable<{ stepUpProof: string }> {
    return this.http.post<{ stepUpProof: string }>(
      `${this.url}/${id}/step-up/verificar`,
      { desafioId, secreto },
      { context: this.stepUpContext() },
    );
  }

  getContextoToken(token: string): Observable<ContextoToken> {
    // SEGURIDAD: el token NO debe viajar en el path/query de la URL — quedaría
    // registrado en access logs (nginx, CDN, proxies), historial del navegador, cache
    // y headers Referer. Se envía en el BODY de un POST, que no se loguea por
    // defecto. La operación sigue siendo idempotente/read-only en el backend.
    return this.http.post<ContextoToken>(`${this.url}/contexto-token`, { token });
  }

  decidirViaToken(token: string, decision: 'aprobar' | 'rechazar', comentario?: string, idempotencyKey?: string): Observable<{ mensaje: string; estadoAprobacion: string }> {
    return this.http.post<{ mensaje: string; estadoAprobacion: string }>(
      `${this.url}/decidir-via-token`,
      { token, decision, comentario },
      idempotencyKey ? { context: withIdempotencyKey(idempotencyKey) } : {},
    );
  }

  reenviar(aprobacionId: string): Observable<Aprobacion> {
    return this.http.patch<Aprobacion>(`${this.url}/${aprobacionId}/reenviar`, {});
  }

  reenviarMail(aprobacionId: string): Observable<{ mensaje: string; destinatarios: number }> {
    return this.http.post<{ mensaje: string; destinatarios: number }>(
      `${this.url}/${aprobacionId}/reenviar-mail`,
      {},
    );
  }
}
